import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const WorkflowProductivityPage = () => {
  const [teamSizeData, setTeamSizeData] = useState(null);
  const [discoveryData, setDiscoveryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTeamSize();
    fetchDiscovery();
  }, []);

  const fetchTeamSize = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/super-admin/admin-portal/team-size-distribution`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTeamSizeData(response.data);
    } catch (err) {
      console.error('Failed to fetch team size:', err);
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDiscovery = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/admin-portal/feature-discovery-timeline`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setDiscoveryData(response.data);
    } catch (err) {
      console.error('Failed to fetch discovery timeline:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Loading workflow analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
          <h1 className="text-3xl font-bold">Workflow & Productivity</h1>
          <p className="text-purple-200 text-sm">Admin efficiency and feature discovery analytics</p>
        </div>
        <div className="bg-red-50 border border-red-300 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={() => {
              fetchTeamSize();
              fetchDiscovery();
            }}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Prepare data for multi-line chart
  const discoveryChartData = [];
  if (discoveryData && discoveryData.feature_timelines) {
    // Get all unique days across all features
    const allDays = new Set();
    Object.values(discoveryData.feature_timelines).forEach(timeline => {
      timeline.forEach(point => allDays.add(point.day));
    });
    
    // Sort days
    const sortedDays = Array.from(allDays).sort((a, b) => a - b);
    
    // Build chart data
    sortedDays.forEach(day => {
      const dataPoint = { day };
      Object.entries(discoveryData.feature_timelines).forEach(([feature, timeline]) => {
        const point = timeline.find(p => p.day === day);
        dataPoint[feature] = point ? point.percentage : 0;
      });
      discoveryChartData.push(dataPoint);
    });
  }

  const featureColors = {
    "Dashboard": "#8b5cf6",
    "Site Management": "#3b82f6",
    "Team Management": "#10b981",
    "Task Management": "#f59e0b",
    "Expenses": "#ef4444",
    "Overtime": "#ec4899",
    "Holidays": "#14b8a6",
    "Payments": "#6366f1",
    "Reports": "#f97316"
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold">Workflow & Productivity</h1>
              <p className="text-purple-200 text-sm">Admin efficiency and feature discovery analytics</p>
            </div>
          </div>
          <button
            onClick={() => {
              fetchTeamSize();
              fetchDiscovery();
            }}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white font-medium transition-all flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Chart 1 (Item 8): Average Team Size Managed */}
      {teamSizeData && teamSizeData.size_distribution && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Size Distribution Across Organisations</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart 
              data={Object.entries(teamSizeData.size_distribution).map(([range, count]) => ({
                range,
                count
              }))}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="range" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#3b82f6" name="Organisations" />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-gray-700">
              <strong>What this shows:</strong> How team sizes are distributed across your customer organisations (1-5 users, 6-10 users, etc.).
            </p>
            <p className="text-sm text-gray-700 mt-2">
              <strong>How to interpret:</strong> Many small teams (1-5) suggests you&apos;re attracting startups or small businesses - ensure features work well at that scale. Many large teams (21-50, 51+) means enterprise customers - prioritize advanced features and scalability. Balanced distribution indicates good market coverage across customer segments. Use this to tailor pricing tiers, feature development, and support resources.
            </p>
          </div>
        </div>
      )}

      {/* Chart 2 (Item 9): Feature Discovery Timeline */}
      {discoveryData && discoveryData.feature_timelines && Object.keys(discoveryData.feature_timelines).length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Feature Discovery Timeline (First 30 Days)</h3>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={discoveryChartData} margin={{ top: 20, right: 10, bottom: 50, left: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" label={{ value: 'Days Since Signup', position: 'insideBottom', offset: -10 }} />
              <YAxis label={{ value: '% Admins Discovered', angle: -90, position: 'left', style: { textAnchor: 'middle' } }} />
              <Tooltip formatter={(value) => `${value}%`} />
              <Legend wrapperStyle={{ paddingTop: '20px' }} />
              {Object.keys(discoveryData.feature_timelines).map(feature => (
                <Line 
                  key={feature}
                  type="monotone" 
                  dataKey={feature} 
                  stroke={featureColors[feature] || '#6366f1'} 
                  strokeWidth={2} 
                  name={feature}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
            <p className="text-sm text-gray-700">
              <strong>What this shows:</strong> The cumulative percentage of admins who have discovered and used each feature over their first 30 days, with each colored line representing a different feature.
            </p>
            <p className="text-sm text-gray-700 mt-2">
              <strong>How to interpret:</strong> Steep curves in the first few days show high discoverability - admins find features quickly (good UX). Flat curves suggest features are hard to find or not prioritized in onboarding. If critical features (Team, Sites) aren&apos;t reaching 80%+ by day 30, improve feature placement, add onboarding prompts, or create guided tours. Compare curves to identify which features need better discovery mechanisms.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowProductivityPage;
